import React from 'react';
import '../assets/styles/FeatureSection.css';

interface FeatureSectionProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  reverse?: boolean;
  showConsultation?: boolean;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  reverse = false,
  showConsultation = false
}) => {
  // Extract the base filename without extension
  const getBasename = (path: string): string => {
    const filename = path.split('/').pop() || '';
    return filename.split('.')[0];
  };
  
  const basename = getBasename(imageSrc);
  const imageExt = imageSrc.split('.').pop() || '';
  
  // Split description into paragraphs
  const paragraphs = description.trim().split(/\n\s*\n/);
  
  return (
    <>
      <section className={`feature-section ${reverse ? 'reverse' : ''}`}>
        <div className="container">
          <div className="feature-wrapper">
            <div className="feature-content">
              <h2>{title}</h2>
              {paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph.trim()}</p>
              ))}
              
              {title.includes("Price on Rails") && (
                <ul className="feature-list">
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Real-time price movement predictions</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>AI-powered analytics constantly fine-tuned</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Works across stocks, futures, and crypto markets</span>
                  </li>
                </ul>
              )}
              
              {title.includes("Agentic Bot") && (
                <ul className="feature-list">
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Automated trading based on Price on Rails signals</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Available on both desktop and web interfaces</span>
                  </li>
                  <li>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#4CAF50"/>
                    </svg>
                    <span>Human oversight with AI-driven precision</span>
                  </li>
                </ul>
              )}
            </div>
            
            <div className="feature-image">
              <picture>
                {/* WebP format */}
                <source
                  type="image/webp"
                  srcSet={`/images/optimized/${basename}.webp`}
                />
                {/* Original format fallback */}
                <img 
                  src={imageSrc} 
                  alt={imageAlt}
                  width="540" 
                  height="360"
                  loading="lazy"
                  decoding="async"
                  style={{
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      
      {showConsultation && (
        <>
          <section className="leadership-section" style={{backgroundColor: '#f8fafc', padding: '80px 0'}}>
            <div className="container">
              <div className="leadership-header" style={{textAlign: 'center', marginBottom: '40px'}}>
                <h2>Leadership Team</h2>
                <p>Meet the experts behind our innovative trading solutions</p>
              </div>
              
              <div className="leadership-team" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '30px',
                maxWidth: '1200px',
                margin: '0 auto'
              }}>
                {/* Team Member 1 */}
                <div className="team-member" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  background: '#ffffff',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                }}>
                  <div style={{
                    width: '220px',
                    height: '220px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    marginBottom: '20px',
                    border: '4px solid rgba(22, 101, 52, 0.2)',
                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img 
                      src="/images/cto-portrait.jpg" 
                      alt="Carlos Martins, CTO & CEO" 
                      style={{width: '100%', height: '100%', objectFit: 'cover'}}
                      loading="lazy"
                    />
                  </div>
                  <h3 style={{fontSize: '1.5rem', marginBottom: '8px', color: '#1e293b', fontWeight: '700'}}>Carlos Martins</h3>
                  <p style={{
                    color: '#2563eb', 
                    fontWeight: '600', 
                    marginBottom: '16px',
                    padding: '5px 12px',
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    borderRadius: '20px',
                    display: 'inline-block'
                  }}>CTO & Interim CEO</p>
                  <p style={{
                    maxWidth: '320px',
                    color: '#64748b',
                    fontSize: '0.95rem',
                    lineHeight: '1.6'
                  }}>
                    AI researcher with extensive experience in financial technologies. MSc in Nuclear Engineering and postgrad in AI from Harvard. Led the development of Price on Rails and oversees strategic direction and technological innovation.
                  </p>
                </div>
                
                {/* Team Member 2 */}
                <div className="team-member" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  background: '#ffffff',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                }}>
                  <div style={{
                    width: '220px',
                    height: '220px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    marginBottom: '20px',
                    border: '4px solid rgba(22, 101, 52, 0.2)',
                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img 
                      src="/images/cbdo-portrait.jpg" 
                      alt="Munir Antum, Chief Business Development Officer" 
                      style={{width: '100%', height: '100%', objectFit: 'cover'}}
                      loading="lazy"
                    />
                  </div>
                  <h3 style={{fontSize: '1.5rem', marginBottom: '8px', color: '#1e293b', fontWeight: '700'}}>Munir Antum</h3>
                  <p style={{
                    color: '#2563eb', 
                    fontWeight: '600', 
                    marginBottom: '16px',
                    padding: '5px 12px',
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    borderRadius: '20px',
                    display: 'inline-block'
                  }}>Chief Business Development Officer</p>
                  <p style={{
                    maxWidth: '320px',
                    color: '#64748b',
                    fontSize: '0.95rem',
                    lineHeight: '1.6'
                  }}>
                    Strategic growth leader with 30+ years in financial markets. Specializes in identifying opportunities, forging partnerships, and driving revenue growth through market expansion and long-term business alliances.
                  </p>
                </div>
                
                {/* Team Member 3 */}
                <div className="team-member" style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  background: '#ffffff',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                }}>
                  <div style={{
                    width: '220px',
                    height: '220px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    marginBottom: '20px',
                    border: '4px solid rgba(22, 101, 52, 0.2)',
                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img 
                      src="/images/cmo-portrait.jpg" 
                      alt="Renato Pelissaro, Chief Marketing Officer" 
                      style={{width: '100%', height: '100%', objectFit: 'cover'}}
                      loading="lazy"
                    />
                  </div>
                  <h3 style={{fontSize: '1.5rem', marginBottom: '8px', color: '#1e293b', fontWeight: '700'}}>Renato Pelissaro</h3>
                  <p style={{
                    color: '#2563eb', 
                    fontWeight: '600', 
                    marginBottom: '16px',
                    padding: '5px 12px',
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    borderRadius: '20px',
                    display: 'inline-block'
                  }}>Chief Marketing Officer</p>
                  <p style={{
                    maxWidth: '320px',
                    color: '#64748b',
                    fontSize: '0.95rem',
                    lineHeight: '1.6'
                  }}>
                    Global strategy and marketing leader with 20+ years' experience at companies such as McKinsey, Microsoft, Yahoo! and PayPal. With a focus on growth, Renato has advised several startups and is known for bridging strategy development and execution.
                  </p>
                </div>
              </div>
            </div>
          </section>
        
          <section className="consultation-section-wrapper" style={{backgroundColor: '#ffffff'}}>
            <div className="consultation-section">
              <div className="booking-header">
                <br />
                <h3>Schedule a <span className="highlight">Free Consultation</span></h3>
                <p>Discover how our platform can enhance your trading strategy with a personalized demo</p>
              </div>
              <div className="booking-card">
                <div className="booking-card-content">
                  <div className="booking-icon">
                    <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M36.6 30.52C35.1 29.02 33.28 29.02 31.8 30.52L28.68 33.64C26.1 32.32 23.7 30.42 21.56 28.26C19.58 26.28 17.68 23.9 16.36 21.32L19.48 18.2C20.98 16.72 20.98 14.9 19.48 13.4L13.4 7.32C11.92 5.84 10.1 5.84 8.6 7.32L5.08 10.86C3.68 12.26 3.12 14.2 3.48 16.08C4.32 20.52 6.94 26.14 11.4 30.6C15.86 35.06 21.48 37.68 25.92 38.52C26.36 38.6 26.8 38.64 27.22 38.64C28.78 38.64 30.26 38.04 31.38 36.92L34.9 33.4C36.38 31.9 36.38 30.08 34.9 28.6L36.6 30.52Z" fill="#2563EB"/>
                    </svg>
                  </div>
                  <h3>Free 30-Minute Consultation</h3>
                  <div className="booking-features">
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span>Personalized platform walkthrough</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span>Q&A with our trading experts</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span>No obligation or pressure</span>
                    </div>
                  </div>
                  <div className="booking-cta">
                    <a href="https://calendly.com/kevin-translatestocks/30min" className="btn-booking">
                      Schedule Your Free Consultation
                    </a>
                    <p className="booking-note">Choose a time that works for you</p>
                  </div>
                </div>
              </div>
            </div>          
          </section>
        </>
      )}
    </>
  );
};

export default FeatureSection;
